import { useState, useEffect } from "react";
import axios from "axios";
import { Wifi, WifiOff, Radio, RefreshCw, Clock, AlertCircle } from "lucide-react";

const API = "/api";

/**
 * Feed Status Indicator
 * 
 * Displays:
 * - Live WebSocket (green) - Real-time Binance WebSocket connected
 * - Polling Fallback (yellow) - Using REST API polling
 * - Disconnected (red) - No data connection
 * - Connecting (blue) - Attempting to connect
 * 
 * Also shows last update timestamp and source
 */
export const FeedStatusIndicator = ({ compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/market/feed-status`);
      setStatus(response.data);
    } catch (e) {
      setStatus({
        status: "disconnected",
        last_update_time: null,
        last_update_source: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const getStatusConfig = (statusObj) => {
    const rawStatus = statusObj?.status;
    const provider = (statusObj?.provider || statusObj?.feed_provider || "").toLowerCase();
    const connected = statusObj?.connected === true || rawStatus === "LIVE";

    if (connected && provider.includes("coinbase")) {
      return {
        icon: <Wifi className="w-3 h-3" />,
        label: "LIVE",
        sublabel: "Coinbase",
        color: "text-green-500",
        bgColor: "bg-green-900/30",
        borderColor: "border-green-700/50",
        pulse: true
      };
    }

    if (connected && provider.includes("kraken")) {
      return {
        icon: <Wifi className="w-3 h-3" />,
        label: "LIVE",
        sublabel: "Kraken",
        color: "text-green-500",
        bgColor: "bg-green-900/30",
        borderColor: "border-green-700/50",
        pulse: true
      };
    }

    if ((rawStatus || "").toUpperCase() === "POLL") {
      return {
        icon: <RefreshCw className="w-3 h-3" />,
        label: "POLL",
        sublabel: "REST",
        color: "text-yellow-500",
        bgColor: "bg-yellow-900/30",
        borderColor: "border-yellow-700/50",
        pulse: false
      };
    }

    if ((rawStatus || "").toUpperCase() === "SIMULATED") {
      return {
        icon: <AlertCircle className="w-3 h-3" />,
        label: "SIM",
        sublabel: "No Live Data",
        color: "text-orange-500",
        bgColor: "bg-orange-900/30",
        borderColor: "border-orange-700/50",
        pulse: false,
        warning: true
      };
    }

    if ((rawStatus || "").toUpperCase() === "CONNECTING") {
      return {
        icon: <Radio className="w-3 h-3 animate-pulse" />,
        label: "...",
        sublabel: "Connecting",
        color: "text-blue-500",
        bgColor: "bg-blue-900/30",
        borderColor: "border-blue-700/50",
        pulse: true
      };
    }

    return {
      icon: <WifiOff className="w-3 h-3" />,
      label: "OFF",
      sublabel: "Disconnected",
      color: "text-red-500",
      bgColor: "bg-red-900/30",
      borderColor: "border-red-700/50",
      pulse: false
    };
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = new Date(ts);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case "binance_ws":
        return "Binance WS";
      case "binance_rest":
        return "Binance REST";
      default:
        return source || "—";
    }
  };

  if (loading || !status) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-zinc-800/50 rounded text-xs font-mono text-zinc-500">
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  const config = getStatusConfig(status);

  if (compact) {
    return (
      <div 
        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono ${config.color} ${config.bgColor} border ${config.borderColor}`}
        title={`${status.provider || config.sublabel} | Last tick: ${formatTimestamp(status.last_tick_timestamp_utc)}`}
        data-testid="feed-status-compact"
      >
        {config.icon}
        <div className="flex flex-col leading-none">
          <span className="font-bold">{config.label}</span>
          {config.warning && <span className="text-[9px] opacity-75">No Live Data</span>}
        </div>
        {status.last_tick_timestamp_utc && (
          <span className="text-[9px] opacity-60 ml-1">
            {formatTimestamp(status.last_tick_timestamp_utc)}
          </span>
        )}
        {config.pulse && (
          <span className="relative flex h-2 w-2 ml-1">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.color.replace('text-', 'bg-')} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2 w-2 ${config.color.replace('text-', 'bg-')}`}></span>
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${config.bgColor} border ${config.borderColor}`}
      data-testid="feed-status-full"
    >
      {/* Status Icon & Label */}
      <div className={`flex items-center gap-2 ${config.color}`}>
        {config.icon}
        <span className="font-mono text-xs font-semibold">{config.label}</span>
        {config.pulse && (
          <span className="relative flex h-2 w-2">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.color.replace('text-', 'bg-')} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2 w-2 ${config.color.replace('text-', 'bg-')}`}></span>
          </span>
        )}
      </div>

      {/* Divider */}
      <div className="w-px h-4 bg-zinc-700"></div>

      {/* Source */}
      <div className="flex items-center gap-1 text-xs text-zinc-400">
        <Radio className="w-3 h-3" />
        <span className="font-mono">{getSourceLabel(status.provider)}</span>
      </div>

      {/* Last Update */}
      <div className="flex items-center gap-1 text-xs text-zinc-500">
        <Clock className="w-3 h-3" />
        <span className="font-mono">{formatTimestamp(status.last_update)}</span>
      </div>

      {/* Symbol/Timeframe */}
      <div className="text-xs font-mono text-zinc-400">
        {status.symbol} {""}
      </div>
    </div>
  );
};

export default FeedStatusIndicator;
